/**
 * Coquille commune aux trois sections.
 *
 * En-tête, navigation, sélecteur de période et états de chargement sont
 * identiques partout : les recopier dans chaque page les ferait diverger à la
 * première correction. La page n'apporte que son contenu.
 *
 * Le sélecteur de période cadre TOUT ce qui est en dessous, sur toutes les
 * sections : les chiffres d'une page et ceux d'une autre décrivent toujours la
 * même tranche de temps.
 */

import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { dataPeriod, dataScopeForPath } from '../lib/dates';
import { formatDayLong } from '../lib/format';
import { usePeriodParam } from '../hooks/usePeriodParam';
import { Layout } from './Layout';
import { PeriodPicker } from './PeriodPicker';
import { periodRulesForPath } from '../lib/periodRules';

interface SectionShellProps {
  /** Fraîcheur des données, remontée par la requête de la page. */
  lastSyncAt: string | null | undefined;
  isPending: boolean;
  isFetching: boolean;
  error: Error | null;
  /** `true` dès que des données sont disponibles, cache compris. */
  hasData: boolean;
  /**
   * Rendu entre le sélecteur de période et le contenu, quel que soit l'état de
   * chargement. Sert au sous-sélecteur d'analyse : il doit rester cliquable
   * pendant qu'une analyse charge, sinon on se retrouve coincé sur une requête
   * lente sans pouvoir en changer.
   */
  beforeContent?: ReactNode;
  /** Titre de la page, au-dessus du sélecteur de période. */
  title?: ReactNode;
  children: ReactNode;
}

export function SectionShell({
  lastSyncAt,
  isPending,
  isFetching,
  error,
  hasData,
  beforeContent,
  title,
  children,
}: SectionShellProps) {
  const { email, signOut } = useAuth();
  const [period, setPeriod] = usePeriodParam();
  // Hier pour l'usage de l'app (sync nocturne), aujourd'hui pour le reste.
  const { pathname } = useLocation();
  const scope = dataScopeForPath(pathname);
  const rules = periodRulesForPath(pathname);
  const effective = dataPeriod(period, scope);

  return (
    <Layout email={email} lastSyncAt={lastSyncAt ?? null} onSignOut={signOut}>
      {title}
      <PeriodPicker value={period} onChange={setPeriod} scope={scope} rules={rules} />

      {beforeContent}

      {error && (
        <p className="notice notice--error" role="alert">
          {error.message}
        </p>
      )}

      {isPending && !error && <p className="notice">Chargement des statistiques...</p>}

      {/* Pendant un rechargement, le contenu precedent reste affiche en
          attenue : pas de squelette, pas de saut de mise en page. */}
      {hasData && <div className={isFetching ? 'is-refetching' : undefined}>{children}</div>}

      {/* Période qui déborde sur les jours à venir : dit en bas, en note. Le
          lecteur vient pour les chiffres, pas pour l'avertissement — il le
          trouve s'il cherche pourquoi la courbe s'arrête avant la fin. */}
      {effective.to < period.to && (
        <p className="footnote">
          Données jusqu'au {formatDayLong(effective.to)}{' '}
          {scope === 'live' ? "(aujourd'hui, journée en cours)" : '(hier)'} : les jours suivants de la
          période n'ont pas encore eu lieu. Les comparaisons portent sur les mêmes jours de la période de
          référence.
        </p>
      )}
    </Layout>
  );
}

/** Période courante, pour les pages qui doivent la passer à leur requête. */
export { usePeriodParam };
